import { toRefs, ref, watch, computed, reactive, unref, toRef } from '@vue/composition-api';
import { useMutation, useQuery } from 'vue-query';
import useVuelidate from '@vuelidate/core';
import {
  AtoneAuthenticatePostResponseSexDivisionEnum,
  AtoneTracksPostRequestAuthenticationTypeEnum,
} from '@netprotections/berlin-bff';
import { BerlinBffContext } from '@services/BerlinBff';
import { createContext, useContext } from '@berlin-front/common/composables/context';
import useOrAll from '@berlin-front/common/composables/useOrAll';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { useSessionId } from '@berlin-front/common/composables/useSessionId';
import useFingerprint from '@berlin-front/common/composables/useFingerprint';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import useErrors from '@composables/useErrors';
import {
  getErrors,
  maxLength,
  regexp,
  REG_PHONE_ONLY_NUMBER_STYLE,
  REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG,
  required,
} from '@berlin-front/common/composables/validator';
import { Location } from 'vue-router';
// import { useGtm } from '@gtm-support/vue2-gtm';
import { event, set } from 'vue-gtag';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { getRefDeep, getRequiredRefValueByPath, notNil, windowOpenNewTab } from '@berlin-front/common/utils';
import { ROUTE_NAME } from '@/router/route-name';
import useIsLegacy from '@composables/useIsLegacy';
import { ModalBridgeContext } from '@services/ModalBridgeContext';

/**
 * atone会員ログイン処理のcomposable
 */
export const useAtoneLogin = () => {
  const sessionId = useSessionId();
  const [isLegacy] = useIsLegacy();
  const router = useRouter();
  const {
    atoneModalParameter,
    setAtoneAuthenticatePostResult,
    tokens,
    setAtoneTracksTrustResult,
    atoneTracksPostResult,
    setAtoneTracksPostResult,
    setAtoneSmsNotifyPostResult,
    atoneTracksTrustResult,
    setAtonePointGetResult,
    setNpTokenAndSignature,
    setPaymentMethodGetResult,
    atoneShopV2OptionGetResult,
  } = useContext(ApiCacheContext);
  const { authenticate, tracks, point, sms, payment } = useContext(BerlinBffContext);
  const { registerMemberFormState, normalize } = useContext(MemberRequestContext);
  const globals = useContext(ModalBridgeContext);
  const fingerprint = useFingerprint();
  const nextPage = ref<Location>();
  const { errors, clearAll } = useErrors({
    fieldMapper: {},
    defaultFieldName: 'flashMessages',
  });

  const messages = computed(() => unref(errors)?.flashMessages ?? []);
  const SERVICE_URL = process.env.VUE_APP_ATONE_TERMS_OF_USE_URL;

  const state = reactive({
    phoneNumber: toRef(registerMemberFormState, 'phoneNumber'),
    password: '',
  });

  // atoneログインの呼び出し(submit)
  const {
    isSuccess: successAtoneAuthenticatePost,
    isError: errorAtoneAuthenticatePost,
    // error,
    // data,
    mutate,
  } = useMutation(
    'atone.login',
    () =>
      authenticate.atoneAuthenticateControllerAuthenticatePost({
        signInId: state.phoneNumber,
        password: state.password,
        shopPublicKey: atoneModalParameter.value.pubKey,
        userNo: atoneModalParameter.value.payment.userNo,
        sessionId,
        isOldParameter: isLegacy.value,
        requestId: globals.requestId,
      }),
    {
      retry: false,
      onSuccess: ({ data: atoneAuthenticatePostResult }) => {
        setAtoneAuthenticatePostResult(atoneAuthenticatePostResult);
        // TODO: スキーマ修正してrequiredにする。
        if (atoneAuthenticatePostResult.token) {
          setNpTokenAndSignature(atoneAuthenticatePostResult.token, atoneAuthenticatePostResult.signature);
        }

        window.newrelic.addPageAction('login_success', {
          member_id: notNil(atoneAuthenticatePostResult.memberCode),
          shop_id: notNil(atoneAuthenticatePostResult.shopId),
          shop_name: notNil(atoneAuthenticatePostResult.shopName),
          gaid: notNil(atoneAuthenticatePostResult.hashId),
        });

        let gender: string | null;
        // eslint-disable-next-line no-underscore-dangle
        if (atoneAuthenticatePostResult.sexDivision === AtoneAuthenticatePostResponseSexDivisionEnum._1) {
          gender = '男性';
          // eslint-disable-next-line no-underscore-dangle
        } else if (atoneAuthenticatePostResult.sexDivision === AtoneAuthenticatePostResponseSexDivisionEnum._2) {
          gender = '女性';
        } else {
          gender = '不明';
        }

        set({
          user_id: notNil(atoneAuthenticatePostResult.memberCode),
          user_properties: {
            member_id: notNil(atoneAuthenticatePostResult.memberCode),
            gender,
            age: atoneAuthenticatePostResult.age,
            shop_id: notNil(atoneAuthenticatePostResult.shopId),
            // shop_name: notNil(atoneAuthenticatePostResult.shopName),
            guest: 'no',
          },
        });

        event('login', {
          method: 'Atone',
        });
      },
    },
  );

  // フィンガープリント登録。ログイン成功したら、呼び出し。
  const { isSuccess: successTrackTokenPost, isError: errorTrackTokenPost } = useQuery(
    'atone.atoneTrackTokenPost',
    () =>
      tracks.atoneTracksControllerTrackTokenPost(getRefDeep(tokens, 'npToken', ''), atoneModalParameter.value.pubKey, {
        authenticationType: AtoneTracksPostRequestAuthenticationTypeEnum.NUMBER_1,
        fingerPrint: fingerprint.value,
      }),
    {
      enabled: computed(() => fingerprint.resolved && unref(successAtoneAuthenticatePost)),
      onSuccess: ({ data }) => {
        setAtoneTracksPostResult(data);
      },
    },
  );

  // フィンガープリント存在チェック。フィンガープリント登録に成功したら、呼び出し。
  const { isSuccess: successTrackTokenTrust, isError: errorTrackTokenTrust } = useQuery(
    'atone.TrackTokenTrust',
    () =>
      tracks.atoneTracksControllerTrackTokenTrust(
        getRefDeep(atoneTracksPostResult, 'trackToken'),
        getRefDeep(tokens, 'npToken'),
        atoneModalParameter.value.pubKey,
      ),
    {
      enabled: computed(() => unref(successTrackTokenPost)),
      onSuccess: ({ data }) => {
        setAtoneTracksTrustResult(data);

        const trustedFlag = atoneTracksTrustResult.value?.trustedFlag;
        if (trustedFlag) {
          // 決済画面へ遷移する。
          nextPage.value = {
            name: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM,
          };
        } else {
          // SMS認証画面へ遷移する。
          nextPage.value = {
            name: ROUTE_NAME.ATONE_AUTH_SMS_SETTLEMENT,
            query: {
              previous: ROUTE_NAME.ATONE_LOGIN_ATONE,
            },
          };
        }
      },
    },
  );

  // SMS送信。フィンガープリント存在チェックでtrustedでない場合に呼び出す。
  const { isSuccess: successSmsNotifyPost, isError: errorSmsNotifyPost } = useQuery(
    'atone.SmsNotifyPost',
    () =>
      sms.atoneSmsControllerSmsNotifyPost(getRefDeep(tokens, 'npToken'), atoneModalParameter.value.pubKey, {
        trackToken: getRefDeep(atoneTracksPostResult, 'trackToken'),
        resend: false,
      }),
    {
      enabled: computed(() => unref(successTrackTokenTrust) && !getRefDeep(atoneTracksTrustResult, 'trustedFlag')),
      onSuccess: ({ data: atoneSmsNotifyPostResult }) => {
        setAtoneSmsNotifyPostResult(atoneSmsNotifyPostResult);
      },
      onError: () => {
        // ログイン時の電話番号を仕掛ける。
        setAtoneSmsNotifyPostResult({
          phoneNumber: state.phoneNumber,
        });
        // SMS認証画面へ遷移する。
        nextPage.value = {
          name: ROUTE_NAME.ATONE_AUTH_SMS_SETTLEMENT,
          query: {
            previous: ROUTE_NAME.ATONE_LOGIN_ATONE,
          },
        };
      },
    },
  );

  // ポイント取得。フィンガープリント存在チェックに成功したら呼び出し。
  const { isSuccess: successAtonePointGet, isError: errorAtonePointGet } = useQuery(
    'atone.atonePointGet',
    () =>
      point.atonePointControllerPointGet(
        getRefDeep(tokens, 'npToken'),
        atoneModalParameter.value.pubKey,
        getRefDeep(tokens, 'trustedTrackToken'),
      ),
    {
      enabled: computed(() => unref(successTrackTokenTrust) && getRefDeep(atoneTracksTrustResult, 'trustedFlag')),
      onSuccess: ({ data: atonePointGetResult }) => {
        setAtonePointGetResult(atonePointGetResult);
      },
    },
  );

  const { isSuccess: successAtonePaymentControllerMethodGet, isError: errorAtonePaymentControllerMethodGet } = useQuery(
    'atone.atonePaymentControllerMethodGet',
    () =>
      payment.atonePaymentControllerMethodGet(
        getRefDeep(tokens, 'npToken'),
        getRefDeep(tokens, 'trustedTrackToken'),
        getRequiredRefValueByPath({ atoneModalParameter }, 'pubKey'),
        getRequiredRefValueByPath({ atoneModalParameter }, 'payment').amount,
        atoneModalParameter.value?.payment?.transactionOptions,
      ),
    {
      enabled: successAtonePointGet,
      onSuccess: ({ data: atoneMethodGetResult }) => {
        setPaymentMethodGetResult(atoneMethodGetResult);
        if (atoneMethodGetResult.splitPayment) {
          // atoneプラス会員の場合
          // atoneプラス会員では分割払いの情報がレスポンスに含まれ、その情報を元にプラス会員用の画面に遷移する。
          if (
            !atoneShopV2OptionGetResult.value?.atoneSplitServiceOption.errors &&
            atoneMethodGetResult.splitPayment.usablePatterns
          ) {
            // 分割利用可能な場合は、支払い会異数の選択画面に遷移する。
            nextPage.value = { name: ROUTE_NAME.ATONE_SPLIT_SELECT };
          } else {
            // 分割利用不可の場合は、一回払いの画面に遷移し利用不可の理由を表示する。
            nextPage.value = { name: ROUTE_NAME.ATONE_SPLIT_SETTLEMENT_ONE_TIME };
          }
        } else {
          // 通常会員の場合は、通常の決済画面に遷移する。
          nextPage.value = { name: ROUTE_NAME.ATONE_SETTLEMENT_CONFIRM };
        }
      },
    },
  );

  const isLoadingApi = ref(false);
  const isError = useOrAll(
    errorAtoneAuthenticatePost,
    errorTrackTokenPost,
    errorTrackTokenTrust,
    errorAtonePointGet,
    errorAtonePaymentControllerMethodGet,
  );
  const isSuccess = useOrAll(
    successSmsNotifyPost, // SMS送信終了後
    errorSmsNotifyPost,
    successAtonePaymentControllerMethodGet, // 分割利用可否判定完了まで
  );

  // 成功、または失敗になったらisEnded
  const isEnded = useOrAll(isSuccess, isError);

  // 進捗。useMutationの状況に合わせてプログレスを進める。
  const {
    progress,
    completed: progressCompleted,
    isLoading: progressIsLoading,
  } = toRefs(useFakeProgress(5000, isLoadingApi, isEnded));

  // バリデーション
  const rules = computed(() => ({
    phoneNumber: {
      required,
      regexp: regexp(REG_PHONE_ONLY_NUMBER_STYLE, REG_PHONE_ONLY_NUMBER_STYLE_ERR_MSG),
    },

    password: {
      required,
      maxLength: maxLength(30),
    },
  }));
  const v$ = useVuelidate(rules, state);

  // そのままmutateをキックするのではなく、
  // - validation結果を確認
  // - 実行中でないことを確認
  // - 実行後（正常系）でないことを確認
  // した上でリクエストを行う。
  const onSubmit = async () => {
    if ((await v$.value.$validate()) && !unref(isLoadingApi) && !unref(isSuccess)) {
      clearAll();
      isLoadingApi.value = true;
      mutate();
    }
  };

  const openMyPageRemind = () => {
    windowOpenNewTab(process.env.VUE_APP_ATONE_PASSWORD_REMIND_URL);
  };

  watch(progressCompleted, (result) => {
    if (result) {
      if (unref(isSuccess) && nextPage.value) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        router.replace(nextPage.value);
        return;
      }
    }
    isLoadingApi.value = false;
  });

  return reactive({
    // 状態
    state,

    // 表示項目
    messages,

    // progress
    progress,
    progressIsLoading,

    // action
    onSubmit,
    openMyPageRemind,
    normalize,

    // validator
    v$,
    getError: getErrors(v$),
    SERVICE_URL,
  });
};

export const AtoneLoginContext = createContext({ useAtoneLogin });
